<script setup>
// Skip links for keyboard navigation accessibility
// These links allow users to skip repetitive content and jump to main content

const skipLinks = [
  {
    id: 'skip-to-main',
    href: '#main',
    label: 'Skip to main content'
  }
]

// Handle skip link click
const handleSkipClick = (event) => {
  event.preventDefault()
  
  // Check if we're in browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }
  
  const targetId = event.currentTarget.getAttribute('href').substring(1)
  const targetElement = document.getElementById(targetId)
  
  if (targetElement) {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    
    // Focus the target element
    targetElement.focus()
    
    // Scroll into view - use 'auto' if user prefers reduced motion
    targetElement.scrollIntoView({ 
      behavior: prefersReducedMotion ? 'auto' : 'smooth', 
      block: 'start' 
    })
  }
}
</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <Teleport to="body">
    <nav class="skip-links" aria-label="Skip links">
      <a
        v-for="link in skipLinks"
        :key="link.id"
        :href="link.href"
        :id="link.id"
        class="skip-link"
        @click="handleSkipClick"
      >
        {{ link.label }}
      </a>
    </nav>
  </Teleport>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>
.skip-links {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10000;
}

.skip-link {
  /* Visually hidden but accessible - positioned off-screen */
  position: fixed;
  top: -100px;
  left: 0;
  z-index: 10001;
  padding: 12px 24px;
  background-color: #000;
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.5;
  border: 2px solid #fff;
  border-radius: 0 0 4px 0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  transition: top 0.2s ease-in-out;
  
  /* Show when focused - move to visible position */
  &:focus,
  &:focus-visible {
    top: 0;
  }
  
  /* Ensure high contrast for visibility */
  &:focus:hover,
  &:focus-visible:hover {
    background-color: #333;
  }
}

/* Ensure skip links work even with reduced motion */
@media (prefers-reduced-motion: reduce) {
  .skip-link {
    transition: none;
  }
  
  .skip-link:focus {
    scroll-behavior: auto;
  }
}
</style>

